import { createAsyncThunk } from "@reduxjs/toolkit";
import { IAuthState, IAuthResponse, ILoginResponse } from "./IAuthState";
import apiClient from "../../../utils/constants/apiClient";
import { toast } from "react-toastify";
import { STATUS_CODE } from "../../../utils/constants/auth";
import api, { apiWithFormDataMethods } from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { ApiResponse } from "../../../types/APIResponse";

interface ILoginProps {
  email: string;
  password: string;
}

interface IUpdateAdminProfileProps {
  firstName: string;
  lastName: string;
  email: string;
}

interface ResetPasswordProps {
  email: string;
  newPassword: string;
  otp: string;
}

export const getProfileInfoThunkAsync = createAsyncThunk("auth/getProfileInfo", async () => {
  try {
    const response: ApiResponse<ILoginResponse> = await api.get(`${apiEndpoints.getProfileInfo}`);
    if (response?.statusCode === STATUS_CODE.SUCCESS) {
      return response.data;
    } else {
      throw new Error("Failed to fetch profile info");
    }
  } catch (error) {
    toast.error("Something went wrong, please try again later!", {
      position: "top-right",
    });
    throw error;
  }
});

export const resetPasswordThunkAsync = createAsyncThunk("auth/resetPassword", async (data: ResetPasswordProps) => {
  let resolveP: (result: IAuthResponse) => void;
  let rejectP: (error: any) => void;

  const resultP = new Promise<IAuthResponse>((resolve, reject) => {
    resolveP = resolve;
    rejectP = reject;
  });

  await api
    .post(`${apiEndpoints.updatePasswordByOTP}`, { data })
    .then((result: any) => {
      const response: IAuthResponse | any = result;
      if (response?.statusCode === STATUS_CODE.SUCCESS) {
        const responseData: IAuthResponse | any = response;
        resolveP(responseData);
        toast.success(response?.message, { position: "top-right" });
      } else {
        toast.error(response?.message);
      }
    })
    .catch((error) => {
      const errorResponse = error?.message;
      toast.error(errorResponse ?? "Something went wrong, please try again later!", {
        position: "top-right",
      });
      rejectP(error);
    });
  return resultP;
});

export const forgotPasswordThunkAsync = createAsyncThunk("auth/forgotPassword", async ({ email }: { email: string }) => {
  let resolveP: (result: IAuthResponse) => void;
  let rejectP: (error: any) => void;

  const resultP = new Promise<IAuthResponse>((resolve, reject) => {
    resolveP = resolve;
    rejectP = reject;
  });

  await api
    .post(`${apiEndpoints.forgotPassword}`, { data: { email } })
    .then((result: any) => {
      const response: IAuthResponse = result;
      if (response?.statusCode === STATUS_CODE.SUCCESS) {
        const responseData: IAuthResponse | any = response;
        resolveP(responseData);
        toast.success("Please check your email id. We sent OTP to your mail id.", { position: "top-right" });
      } else {
        toast.error("User not found, please enter valid email/password");
      }
    })
    .catch((error) => {
      toast.error(error?.message || "Something went wrong, please try again later!", {
        position: "top-right",
      });
      rejectP(error);
    });
  return resultP;
});

export const signInThunkAsync = createAsyncThunk("auth/authenticate", async (data: ILoginProps) => {
  try {
    const response: ApiResponse<ILoginResponse> = await api.post(`${apiEndpoints.login}`, { data });
    if (response?.statusCode === STATUS_CODE.SUCCESS) {
      const responseData = response.data;
      return responseData;
    } else {
      toast.error("User not found, please enter valid email/password");
      throw new Error("User not found");
    }
  } catch (error: any) {
    toast.error(error.message || "Something went wrong, please try again later!", {
      position: "top-right",
    });
    throw error?.message || "Failed to fetch categories";
  }
});

///api/admin/updateAdminProfile
export const updateAdminProfileThunkAsync = createAsyncThunk<ILoginResponse, IUpdateAdminProfileProps, { rejectValue: string }>("auth/updateAdminProfile", async (data, { rejectWithValue }) => {
  try {
    const response: ApiResponse<ILoginResponse> = await api.put(`${apiEndpoints.updateAdminProfile}`, { data });
    return response.data;
  } catch (error: any) {
    console.error({ error });
    return rejectWithValue(error.message || "Failed to update profile");
  }
});

// Update Admin User Password
export const updateAdminUserPassword = createAsyncThunk<ILoginResponse, { currentPassword: string; newPassword: string }, { rejectValue: string }>(
  "auth/updateAdminUserPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<ILoginResponse>>(`${apiEndpoints.updatePasswordByCurrentPassword}`, { data });
      return response.data;
    } catch (error: any) {
      console.error({ error });
      return rejectWithValue(error.message || "Failed to update password");
    }
  }
);
